import type React from 'react';
import {
  LayoutDashboard, PackageX, Bug, Building2, Gauge, CreditCard, CheckSquare, ClipboardCheck, Landmark, Banknote, Mail, Boxes, ArrowLeftRight, TrendingUp,
  Scale, FileStack, AlertTriangle, Sun, Unlink, Search, GitCompare, Activity, BarChart3, Eye, ExternalLink, Settings,
  Users, UserCheck, UserCog, FileText, FileArchive, Calendar, Cog, ShieldCheck, FileCode, MailOpen, Coins, Key, Sliders,
  HelpCircle, PoundSterling, ShieldAlert, ListChecks, AlertOctagon, CalendarClock, BookOpen, CheckCheck, Network, Grid3x3, List,
  LayoutGrid, Lock, AtSign, Send, Tags, Bot, Contact, Hourglass, Inbox, BadgePercent, Fingerprint, Upload,
} from 'lucide-react';

/**
 * The one menu definition behind the sidebar, desktop rail and phone drawer alike. Groups hold sections of items;
 * module- and role-gating is per item (and optionally per group); a group shows only if one of its items does.
 * buildNav() turns it into what a given user sees; Sidebar.tsx renders that.
 *
 * No routes change here: every href is an existing route in App.tsx.
 */

type Icon = React.ComponentType<{ className?: string }>;

export interface NavItem {
  label: string;
  href: string;
  icon: Icon;
  module?: string;          // shown only when this module is enabled
  admin?: boolean;          // shown only to admins
  roles?: string[];         // shown only to users with one of these roles (admins always have 'admin')
  notAdmin?: boolean;       // hidden from admins (they reach the page from Settings): no item twice
  external?: boolean;       // opens in a new tab (links from runtime config)
  match?: string;           // path prefix for the active state, when it differs from href
  description?: string;     // C67: one line on the Settings hub
  borrowed?: boolean;       // another module's page listed here too: it never keeps a group in the menu on its own
}

export interface NavSection {
  title?: string;
  items: NavItem[];
}

export interface NavGroup {
  id: string;
  label: string;
  icon: Icon;
  sections: NavSection[];
  module?: string;
  admin?: boolean;
  match?: string[];         // extra path prefixes that mark the group active
}

export type NavEntry = { kind: 'link'; item: NavItem } | { kind: 'group'; group: NavGroup };

/** Modules that add their own sections. With none of them on (Cint today), Sign-off stays two flat links. */
export const ADDON_MODULES = ['sbl', 'bankrec', 'cashapp', 'close', 'intercompany'];

export interface ReportLink { label: string; url: string }

export const DASHBOARD: NavItem = { label: 'Dashboard', href: '/dashboard', icon: LayoutDashboard };
/** 3 Nov (Davin: "Yes to the upload statement link in the cash app menu"): Bank reconciliation's statement upload,
 *  under Cash application too. Only where Bank reconciliation is on and for the roles that may prepare (the upload
 *  needs RECONCILE_PREPARE): never a link that answers 403. The Workbench shows it as a header button the same way. */
export const PREPARE_ROLES = ['admin', 'preparer', 'senior_approver'];
export const UPLOAD_STATEMENTS: NavItem = { label: 'Upload statements', href: '/bankrec/upload', icon: Upload, module: 'bankrec',
                                            roles: PREPARE_ROLES, borrowed: true };
export const HELP: NavItem = { label: 'Help', href: '/help', icon: HelpCircle };
/** C5: the approvals inbox, top level after Dashboard (its count is the badge): approvers decide there; preparers see
 *  their own submissions waiting, read-only (Davin, 14 Oct). */
/** C67: Admin left the menu; this one item at the foot of the nav opens the Settings hub (/admin), which lists every
 *  admin page (SETTINGS_SECTIONS). It stays highlighted on any /admin page that isn't elsewhere in the menu. */
export const SETTINGS: NavItem = { label: 'Settings', href: '/admin', icon: Settings, admin: true };
export const APPROVALS: NavItem = { label: 'Approvals', href: '/approvals', icon: CheckCheck,
                                   roles: ['admin', 'approver', 'senior_approver', 'preparer'] };

export function navGroups(reportLinks: ReportLink[] = []): NavGroup[] {
  return [
    {
      id: 'signoff', label: 'Reconciliation', icon: CheckSquare,
      sections: [{ items: [
        { label: 'Accounts', href: '/accounts', icon: Building2, module: 'reconciliation' },
        { label: 'Reconciliations', href: '/reconciliation', icon: CheckSquare, module: 'reconciliation' },
        { label: 'Balance sheet', href: '/balance-sheet', icon: Scale, module: 'reconciliation' },
        { label: 'Month-end close', href: '/close', icon: ClipboardCheck, module: 'close' },
      ] }, {
        // the reconciliation reports live with reconciliation (Davin, 14 Oct); admin-only, as their routes are
        title: 'Reports', items: [
          { label: 'Executive dashboard', href: '/reports/executive-dashboard', icon: Eye, module: 'reconciliation', admin: true },
          { label: 'Staff performance', href: '/reports/staff-performance', icon: TrendingUp, module: 'reconciliation', admin: true },
          // read-only round (2 Nov): auditors' pack where reports are looked for, as well as under Settings
          { label: 'Audit pack', href: '/admin/audit-pack', icon: FileArchive, module: 'reconciliation', admin: true },
          // K-07: auditors read the trail; an admin who is also an auditor has it in Settings, not twice
          { label: 'Audit trail', href: '/admin/audit', icon: FileText, roles: ['auditor'], notAdmin: true },
        ] }],
    },
    {
      id: 'intercompany', label: 'Intercompany', icon: Network, module: 'intercompany',
      sections: [{ items: [
        { label: 'Matrix', href: '/intercompany', icon: Grid3x3 },
        { label: 'Pairs', href: '/intercompany/pairs', icon: List },
        { label: 'Unassigned', href: '/intercompany/unassigned', icon: HelpCircle },
        { label: 'Health', href: '/intercompany/health', icon: Activity },
        { label: 'Elimination', href: '/intercompany/elimination', icon: Scale },
        { label: 'Disputes', href: '/intercompany/disputes', icon: AlertTriangle },
        { label: 'Approvals', href: '/intercompany/approvals', icon: CheckSquare, roles: ['admin', 'approver', 'senior_approver', 'manager'] },
        { label: 'Uploads', href: '/intercompany/uploads', icon: FileStack, roles: ['admin', 'preparer', 'approver', 'senior_approver', 'manager'] },
        { label: 'Set-up', href: '/intercompany/setup', icon: Sliders, admin: true },
      ] }],
    },
    {
      id: 'bankrec', label: 'Bank reconciliation', icon: Landmark, module: 'bankrec',
      sections: [{ items: [
        { label: 'Bank reconciliation', href: '/bankrec', icon: Landmark },
      ] }],
    },
    {
      id: 'cashapp', label: 'Cash application', icon: PoundSterling, module: 'cashapp',
      // C67: short lists under small titles, so the longest open group still reads at a glance. Compact menu (30 Oct,
      // Davin): the reports have their own section, and each title folds its list away (Sidebar.tsx, Setup folded at first)
      sections: [{ title: 'Daily work', items: [
        { label: 'Today', href: '/cashapp/today', icon: Sun },       // review 2 Nov: the day's work in order
        { label: 'Overview', href: '/cashapp/overview', icon: LayoutGrid },
        { label: 'Workbench', href: '/cashapp', icon: Banknote },
        { label: 'Apply proposals', href: '/cashapp/apply-proposals', icon: ClipboardCheck },
        // 2 Nov (Davin: "Lets switch now"): the new Remittances page and the Inbox for everyone with the cash app. The
        // old Remittances list, Held emails, Received emails and Customer requests redirect (App.tsx, oldPages.tsx);
        // Remittance review is retired into the match card (Phase 4b): its address opens Remittances at Rows to decide
        { label: 'Remittances', href: '/cashapp/remittances/new', icon: Mail },
        { label: 'Inbox', href: '/cashapp/inbox', icon: Inbox },   // held emails and requests waiting are its badge
        { label: 'Card settlements', href: '/cashapp/card-settlements', icon: CreditCard },
        UPLOAD_STATEMENTS,
      ] }, { title: 'Collections', items: [
        { label: 'Credit control', href: '/cashapp/credit', icon: ShieldAlert },
        // 2 Nov (order release, Phase 1): system administrators first, while it's tried
        { label: 'Orders on hold', href: '/cashapp/orders-on-hold', icon: PackageX, roles: ['sysadmin'] },
        // 4 Nov: people's corrected readings, for system administrators to review (the Inbox's reading problems)
        { label: 'Reading corrections', href: '/cashapp/inbox/reports', icon: Bug, roles: ['sysadmin'] },
        { label: 'Worklist', href: '/cashapp/worklist', icon: ListChecks },
        { label: 'Activities', href: '/cashapp/activities', icon: CalendarClock },
        { label: 'Disputes', href: '/cashapp/disputes', icon: AlertOctagon },
        { label: 'CreditSafe', href: '/cashapp/creditsafe', icon: Gauge },
        // 3 Nov (Davin): cover while someone is away is a delegation, never a reassignment (cover.py)
        { label: 'Delegations', href: '/cashapp/delegations', icon: UserCog },   // the Delegations page, under the cash menu
      ] }, { title: 'Reports', items: [
        { label: 'Aged debt', href: '/cashapp/aged-debt', icon: Hourglass },
        { label: 'Receipts day book', href: '/cashapp/receipts-day-book', icon: BookOpen },
        { label: 'Payments to apply', href: '/cashapp/unapplied-payments', icon: Coins },
        { label: 'Scorecard', href: '/cashapp/reports/scorecard', icon: Scale },
      ] }, { title: 'Setup', items: [
        { label: 'Payer names', href: '/cashapp/payer-names', icon: Contact },
        { label: 'Customer fingerprints', href: '/cashapp/fingerprints', icon: Fingerprint },
        { label: 'Settlement discounts', href: '/cashapp/settlement-discounts', icon: BadgePercent },
        { label: 'Chase templates', href: '/cashapp/chase-templates', icon: MailOpen, admin: true },
      ] }],
    },
    {
      id: 'stock', label: 'Stock', icon: Boxes, module: 'sbl',
      sections: [{ items: [
        { label: 'Stock position', href: '/sbl', icon: Boxes },
        { label: 'Movements', href: '/sbl/movements', icon: ArrowLeftRight },
        { label: 'Gross profit', href: '/sbl/gross-profit', icon: TrendingUp },
        { label: 'Reconciliations (GRNI etc.)', href: '/sbl/reconciliations/grni', icon: Scale, match: '/sbl/reconciliations' },
        { label: 'Open documents', href: '/sbl/open-documents/grni/stock', icon: FileStack, match: '/sbl/open-documents' },
        { label: 'Exceptions', href: '/sbl/exceptions', icon: AlertTriangle },
        { label: 'Ledger proof', href: '/sbl/ledger-proof', icon: ShieldCheck },
        { label: 'Unlinked', href: '/sbl/unlinked', icon: Unlink },
        { label: 'Transaction viewer', href: '/sbl/transactions', icon: Search },
        { label: 'Parallel run', href: '/sbl/parallel-run', icon: GitCompare },
        { label: 'Data health', href: '/sbl/data-health', icon: Activity },
      ] }],
    },
    {
      // the page's own route guard (App.tsx): approvers post journals too, so it isn't an admin-only item any more
      id: 'journals', label: 'Journals', icon: BookOpen, module: 'reconciliation',
      sections: [{ items: [
        { label: 'Journals', href: '/admin/journals', icon: BookOpen, roles: ['admin', 'approver', 'senior_approver'] },
      ] }],
    },
    {
      // the client's own reporting (REPORT_LINKS, e.g. "Norbain reporting"): one link shows as a plain link
      id: 'reports', label: 'Reports', icon: BarChart3, admin: true,
      sections: [{ items: reportLinks.map(l => ({ label: l.label, href: l.url, icon: ExternalLink, external: true })) }],
    },
  ];
}

/** C67: the Settings hub (/admin): every admin page, grouped, each with one line on what it's for. Not in the menu;
 *  ⌘K finds them too. Gating as the menu's (module, role); every href is an existing route. */
export const SETTINGS_SECTIONS: NavSection[] = [
  { title: 'People', items: [
    { label: 'Users', href: '/admin/users', icon: Users, description: 'Add people, set their roles, and turn sign-in off.' },
    { label: 'Sign-in & security', href: '/admin/sign-in-security', icon: Lock,
      description: 'Which ways in this site offers: password, Microsoft, Google, Okta.' },
    { label: 'Allowed e-mail domains', href: '/admin/mail-domains', icon: AtSign,
      description: 'Which e-mail domains this site sends invitations and notifications to.' },
    { label: 'Assignments', href: '/admin/assignments', icon: UserCheck, module: 'reconciliation',
      description: 'Who prepares and who approves each account.' },
    { label: 'Delegations', href: '/admin/delegations', icon: UserCog, description: "Cover for someone's work while they're away." },
    { label: 'Audit trail', href: '/admin/audit', icon: FileText, description: 'Who did what, and when, across the system.' },
    { label: 'Access review', href: '/admin/access-reviews', icon: UserCheck,
      description: 'Check who can do what; a second administrator applies each change.' },
    { label: 'Audit pack', href: '/admin/audit-pack', icon: FileArchive,
      description: 'One zip for your external auditor: access, sign-offs and the audit trail.' },
  ] },
  { title: 'Periods & accounts', items: [
    { label: 'Periods', href: '/admin/periods', icon: Calendar, description: 'Open, close and lock month-end periods.' },
    { label: 'Account configuration', href: '/admin/accounts', icon: Cog, module: 'reconciliation',
      description: 'Which accounts need reconciling, their templates, risk and due days.' },
    { label: 'Import queue', href: '/admin/import-queue', icon: ListChecks, description: 'NetSuite import requests and the API log.' },
  ] },
  { title: 'Rules & templates', items: [
    { label: 'Auto-cert rules', href: '/admin/auto-cert-rules', icon: ShieldCheck, module: 'reconciliation',
      description: 'When a reconciliation may sign itself off.' },
    { label: 'Balance sheet settings', href: '/admin/balance-sheet-settings', icon: Scale, module: 'reconciliation',
      description: 'The movement threshold and the financial year start.' },
    { label: 'Templates', href: '/admin/templates', icon: FileCode, module: 'reconciliation',
      description: 'The reconciliation templates and their fields.' },
    { label: 'Email templates', href: '/admin/email-templates', icon: MailOpen, description: 'The wording of the emails the system sends.' },
  ] },
  { title: 'Currencies', items: [
    { label: 'Currencies', href: '/admin/currencies', icon: Coins, description: 'Currencies in use and their exchange rates.' },
  ] },
  { title: 'Integrations & checks', items: [
    { label: 'API keys', href: '/admin/api-keys', icon: Key, description: 'Keys for the feeds that send data in.' },
    { label: 'Journal settings', href: '/admin/journal-settings', icon: FileText, module: 'reconciliation',
      description: 'Evidence and approval limits for journals, and the NetSuite import file they go out in.' },
    { label: 'Write to NetSuite', href: '/admin/netsuite-writeback', icon: Send,
      description: 'Whether approved journals and payments are created in NetSuite, and what has been sent.' },
    { label: 'NetSuite balance check', href: '/admin/balance-check', icon: Scale,
      description: "Run the check that MatchPoint's balances tie to NetSuite's, and see the last result." },
    { label: 'Balance variance report', href: '/admin/balance-variance', icon: AlertTriangle,
      description: "NetSuite's balances against the balances MatchPoint works out." },
  ] },
  { title: 'Modules', items: [
    { label: 'Bank accounts', href: '/admin/bank-accounts', icon: Landmark, module: 'bankrec',
      description: 'The bank accounts reconciled, and their GL accounts.' },
    { label: 'Bank rec settings', href: '/admin/bankrec-settings', icon: Sliders, module: 'bankrec',
      description: 'Matching tolerances, coding rules and journal limits for bank reconciliation.' },
    { label: 'Chase templates', href: '/cashapp/chase-templates', icon: MailOpen, module: 'cashapp',
      description: 'The letters credit control sends customers.' },
    { label: 'Cash app automation', href: '/admin/cashapp-automation', icon: Bot, module: 'cashapp',
      description: 'Automatic matching, re-matching and how remittance documents are read.' },
    { label: 'Credit controllers', href: '/admin/credit-controllers', icon: UserCheck, module: 'cashapp',
      description: 'Which MatchPoint user is each NetSuite credit controller (by email address).' },
    { label: 'Customer requests', href: '/admin/customer-requests', icon: Inbox, module: 'cashapp',
      description: 'How replies to customers asking for statements and invoice copies are handled.' },
    { label: 'Group companies', href: '/admin/group-companies', icon: Building2, module: 'cashapp', roles: ['sysadmin'],
      description: "The group's other companies: advices paying them are marked Not ours instead of held." },
    { label: 'Email labels', href: '/admin/email-labels', icon: Tags, module: 'cashapp',
      description: 'Check a sample of the emails MatchPoint decided were not remittances.' },
    { label: 'Remittance feed health', href: '/admin/remittance-feed-health', icon: Activity, module: 'cashapp',
      description: 'Remittance advices arriving from emails: how many, how they were read, and their warnings.' },
  ] },
  { title: 'Help', items: [
    { label: 'Help content', href: '/help/admin', icon: HelpCircle, description: 'The help pages, as published.' },
  ] },
];

export interface NavContext {
  hasModule: (name: string) => boolean;
  isAdmin: boolean;
  roles?: string[];
  reportLinks?: ReportLink[];
  /** Per-deployment menu hiding (config.js HIDE_NAV): href prefixes, by whole path segments, left out of the menu.
   *  Menu only: the pages still open by direct link, and the modules stay on (their APIs and feeds keep working). */
  hideNav?: string[];
}

const prefixes = (h: unknown): string[] =>
  Array.isArray(h) ? h.filter((p): p is string => typeof p === 'string' && p.startsWith('/') && p !== '/') : [];

/** config.js SYSADMIN_ONLY_NAV (31 Oct, Davin: hide some features "from anyone except sys_admin for now"): href
 *  prefixes shown only to sysadmins; everyone else, admins included, neither sees them in the menus nor opens them
 *  (SysadminOnlyGuard). Empty or unset: nothing changes. */
export function configuredSysadminOnly(): string[] {
  return prefixes(typeof window !== 'undefined' ? window.APP_CONFIG?.SYSADMIN_ONLY_NAV : undefined);
}

/** True when ``href`` is kept for sysadmins on this deployment and this user isn't one. */
export const sysadminOnly = (href: string, roles: string[] = []) =>
  !roles.includes('sysadmin') && configuredSysadminOnly().some(p => pathMatches(href.split('?')[0], p));

/** config.js HIDE_NAV, cleaned: path prefixes only ('/' itself is ignored, it would hide everything). With ``roles``,
 *  SYSADMIN_ONLY_NAV too, unless the user is a sysadmin. */
export function configuredHideNav(roles?: string[]): string[] {
  const h = prefixes(typeof window !== 'undefined' ? window.APP_CONFIG?.HIDE_NAV : undefined);
  return roles === undefined || roles.includes('sysadmin') ? h : [...h, ...configuredSysadminOnly()];
}

/** True when this deployment hides ``href`` from its menus (other in-page tab bars use it too, e.g. SBL's). */
export const hiddenPath = (href: string, hide: string[] = configuredHideNav()) =>
  hide.some(p => pathMatches(href.split('?')[0], p));

const hiddenByConfig = (i: NavItem, ctx: NavContext) =>
  !i.external && (ctx.hideNav ?? []).some(p => pathMatches(i.href.split('?')[0], p));

/** The Settings hub's sections this user sees (empty unless an admin). */
export function settingsSections(ctx: NavContext): NavSection[] {
  if (!visible(SETTINGS, ctx)) return [];
  return SETTINGS_SECTIONS
    .map(s => ({ ...s, items: s.items.filter(i => visible(i, ctx) && !hiddenByConfig(i, ctx)) }))
    .filter(s => s.items.length > 0);
}

const visible = (x: { module?: string; admin?: boolean; notAdmin?: boolean; roles?: string[] }, ctx: NavContext) =>
  (!x.module || ctx.hasModule(x.module)) && (!x.admin || ctx.isAdmin) && (!x.notAdmin || !ctx.isAdmin)
  && (!x.roles || x.roles.some(r => (ctx.roles ?? (ctx.isAdmin ? ['admin'] : [])).includes(r)));

/** Whether this user sees Upload statements (UPLOAD_STATEMENTS): the Workbench's header button follows the menu. */
export const mayUploadStatements = (ctx: Pick<NavContext, 'hasModule' | 'isAdmin' | 'roles'>): boolean =>
  visible(UPLOAD_STATEMENTS, ctx as NavContext);

/** What this user sees: Dashboard, the groups that have at least one visible item (sections without visible
 *  items dropped), then Settings (admins), then Help. A group left with one item is that item as a plain link (Bank reconciliation,
 *  Journals); on a core-only instance (no add-on module) Reconciliation's items are flat links, as before, its
 *  reports after them. */
export function buildNav(ctx: NavContext): NavEntry[] {
  const coreOnly = !ADDON_MODULES.some(m => ctx.hasModule(m));
  const out: NavEntry[] = [{ kind: 'link', item: DASHBOARD }];
  if (visible(APPROVALS, ctx) && !hiddenByConfig(APPROVALS, ctx)) out.push({ kind: 'link', item: APPROVALS });
  for (const g of navGroups(ctx.reportLinks)) {
    if (!visible(g, ctx)) continue;
    const sections = g.sections
      .map(s => ({ ...s, items: s.items.filter(i => visible(i, ctx) && !hiddenByConfig(i, ctx)) }))
      .filter(s => s.items.length > 0);
    if (sections.length === 0 || sections.every(s => s.items.every(i => i.borrowed))) continue;
    if (g.id === 'signoff' && coreOnly) {
      for (const s of sections) {
        for (const i of s.items) {
          out.push({ kind: 'link', item: i.href === '/reconciliation' ? { ...i, label: 'Reconciliation' } : i });
        }
      }
      continue;
    }
    const items = sections.flatMap(s => s.items);
    if (items.length === 1 && !g.match) {
      out.push({ kind: 'link', item: items[0] });
      continue;
    }
    out.push({ kind: 'group', group: { ...g, sections } });
  }
  if (visible(SETTINGS, ctx)) out.push({ kind: 'link', item: SETTINGS });
  out.push({ kind: 'link', item: HELP });
  return out;
}

const prefixOf = (i: NavItem) => i.match ?? i.href;

export function pathMatches(pathname: string, prefix: string): boolean {
  if (prefix === '/') return pathname === '/';
  return pathname === prefix || pathname.startsWith(prefix.endsWith('/') ? prefix : prefix + '/');
}

/** Pages that aren't in the menu and belong to an item other than the one their path prefix would pick. A customer
 *  (/cashapp/customers/:code) is opened from Credit control or the Worklist, not the Workbench (/cashapp). */
export const ACTIVE_ALIASES: { prefix: string; href: string }[] = [
  { prefix: '/cashapp/customers', href: '/cashapp/credit' },
];

// 31 Oct review #17: the last page shown that isn't an alias (a customer page, Apply), so one opened by a link that
// doesn't say where it came from (location.state.from) still highlights, and leads back to, that page. Noted by the
// Sidebar on every navigation; lost on reload (then the alias's own item, as before).
let LAST_PAGE = '';
export function notePage(pathname: string, search = ''): void {
  if (!ACTIVE_ALIASES.some(a => pathMatches(pathname, a.prefix))) LAST_PAGE = pathname + search;
}
export const lastPage = (): string => LAST_PAGE;

/** The path whose menu item is highlighted: the page's own, or for an alias the page it was opened from
 *  (location.state.from, as the customer page's Back link reads it: a path, or a Credit control query string), else
 *  the alias's item. */
export function activePath(pathname: string, from?: unknown): string {
  const alias = ACTIVE_ALIASES.find(a => pathMatches(pathname, a.prefix));
  if (!alias) return pathname;
  if (from === undefined || from === null || from === '') from = lastPage();     // 31 Oct review #17
  if (typeof from === 'string' && from.startsWith('/')) {
    const p = from.split('?')[0];
    if (!ACTIVE_ALIASES.some(a => pathMatches(p, a.prefix))) return p;
  }
  return alias.href;
}

/** The one item to highlight for a path: the visible item with the longest matching prefix, so
 *  /cashapp/remittances marks Remittances, not Cash application, and /sbl/movements marks Movements. */
export function activeItem(pathname: string, entries: NavEntry[]): NavItem | null {
  let best: NavItem | null = null;
  const consider = (i: NavItem) => {
    if (i.external) return;
    const p = prefixOf(i);
    if (pathMatches(pathname, p) && (!best || p.length > prefixOf(best).length)) best = i;
  };
  for (const e of entries) {
    if (e.kind === 'link') consider(e.item);
    else e.group.sections.forEach(s => s.items.forEach(consider));
  }
  return best;
}

/** A group is active when the active item is one of its items, or, when no menu item matches, the path falls under
 *  one of its extra prefixes (e.g. /admin pages not in the menu, like the import queue; but /admin/journals is
 *  Journals', not Admin's). */
export function isGroupActive(group: NavGroup, pathname: string, active: NavItem | null): boolean {
  if (active) return group.sections.some(s => s.items.includes(active));
  return (group.match ?? []).some(p => pathMatches(pathname, p));
}
